import { z } from 'zod';
import {
  VENDOR_DELIVERY_CYCLES,
  VENDOR_DELIVERY_SCOPES,
  VENDOR_FREIGHT_RULES,
  VENDOR_INVOICE_DELIVERY_METHODS,
  VENDOR_INVOICE_FORMATS,
  VENDOR_PAYMENT_METHODS,
  VENDOR_PAYMENT_TERMS,
  VENDOR_PRODUCT_CATEGORY_CODES,
  VENDOR_RETURN_CONDITION_TYPES,
  VENDOR_RETURN_FREIGHT_RESPONSIBILITIES,
  VENDOR_RETURN_HANDLING_METHODS,
  VENDOR_WEEKDAYS,
} from './vendor-profile';

const optionalNumber = z.number().finite().nonnegative().nullable().optional();
const optionalInteger = z.number().int().nonnegative().nullable().optional().default(null);
const optionalDecimal = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value === null ? null : String(value)),
  z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, '金額格式不正確。')
    .nullable(),
);
const stringArray = z.array(z.string().trim().min(1).max(80)).max(40).default([]);

const vendorProfileRequiredText = (max: number) => z.string().trim().min(1).max(max);
const vendorProfileNumber = z.number().finite().nonnegative().nullable();

const vendorBusinessProfileSchema = z.object({
  company_name: vendorProfileRequiredText(160),
  company_short_name: z.string().trim().max(80).nullable().default(null),
  tax_id: vendorProfileRequiredText(20),
  phone: vendorProfileRequiredText(40),
  address: vendorProfileRequiredText(240),
  company_email: z.string().trim().email().max(160),
  website: z.string().trim().max(240).default(''),
  company_fax: z.string().trim().max(40).default(''),
  company_intro: z.string().max(3000).default(''),
  notes: z.string().max(2000).default(''),
  principal_brands: z.array(z.string().trim().min(1).max(160)).min(1).max(40),
  supplied_product_categories: z
    .array(z.enum(VENDOR_PRODUCT_CATEGORY_CODES))
    .min(1)
    .max(VENDOR_PRODUCT_CATEGORY_CODES.length),
});

const vendorContactProfileSchema = z.object({
  contact_name: vendorProfileRequiredText(80),
  contact_job_title: z.string().trim().max(80).nullable().default(null),
  contact_phone: vendorProfileRequiredText(40),
  contact_email: z.string().trim().email().max(160),
  vendor_closing_day: vendorProfileRequiredText(20),
  daily_order_cutoff_time: vendorProfileRequiredText(10),
});

const vendorPaymentTermsProfileSchema = z.object({
  payment_term: z.enum(VENDOR_PAYMENT_TERMS),
  monthly_payment_days: vendorProfileNumber,
  payment_method: z.enum(VENDOR_PAYMENT_METHODS),
  payment_method_other: z.string().trim().max(300).default(''),
  weekly_payment_note: z.string().trim().max(500).default(''),
  payment_rule_acknowledged: z.boolean().default(false),
});

const vendorDeliveryTermsProfileSchema = z.object({
  delivery_scope: z.enum(VENDOR_DELIVERY_SCOPES),
  delivery_cycle: z.enum(VENDOR_DELIVERY_CYCLES),
  delivery_weekdays: z.array(z.enum(VENDOR_WEEKDAYS)).max(VENDOR_WEEKDAYS.length).default([]),
  delivery_cycle_other: z.string().trim().max(300).default(''),
  vendor_closing_day: vendorProfileRequiredText(20),
  daily_order_cutoff_time: vendorProfileRequiredText(10),
  minimum_order_amount: vendorProfileNumber,
  minimum_order_quantity: z.number().int().nonnegative().nullable(),
  freight_rule: z.enum(VENDOR_FREIGHT_RULES),
  freight_threshold_amount: vendorProfileNumber,
  freight_fixed_amount: vendorProfileNumber,
  freight_other_note: z.string().trim().max(500).default(''),
});

const vendorReturnTermsProfileSchema = z.object({
  return_scope: z.enum(VENDOR_DELIVERY_SCOPES),
  return_handling_method: z.enum(VENDOR_RETURN_HANDLING_METHODS),
  return_condition_types: z
    .array(z.enum(VENDOR_RETURN_CONDITION_TYPES))
    .min(1)
    .max(VENDOR_RETURN_CONDITION_TYPES.length),
  return_near_expiry_condition: z.string().trim().max(500).default(''),
  return_defect_condition: z.string().trim().max(500).default(''),
  return_intolerance_condition: z.string().trim().max(500).default(''),
  return_other_condition: z.string().trim().max(500).default(''),
  return_condition_description: vendorProfileRequiredText(2000),
  return_freight_responsibility: z.enum(VENDOR_RETURN_FREIGHT_RESPONSIBILITIES),
  return_freight_rule_note: z.string().trim().max(1000).default(''),
});

const vendorInvoiceTermsProfileSchema = z.object({
  invoice_format: z.enum(VENDOR_INVOICE_FORMATS),
  invoice_format_other: z.string().trim().max(300).default(''),
  invoice_delivery_method: z.enum(VENDOR_INVOICE_DELIVERY_METHODS),
  invoice_delivery_other: z.string().trim().max(500).default(''),
  invoice_same_as_primary: z.boolean().default(true),
  invoice_contact_name: z.string().trim().max(80).default(''),
  invoice_contact_phone: z.string().trim().max(40).default(''),
  invoice_email: z.string().trim().max(160).email().or(z.literal('')).default(''),
  reconciliation_same_as_primary: z.boolean().default(true),
  reconciliation_contact_name: z.string().trim().max(80).default(''),
  reconciliation_contact_phone: z.string().trim().max(40).default(''),
  reconciliation_email: z.string().trim().max(160).email().or(z.literal('')).default(''),
  reconciliation_terms_acknowledged: z.boolean().default(false),
});

const vendorBankDetailsSchema = z.object({
  id: z.string().trim().max(100).optional(),
  bank_name: z.string().trim().max(120).default(''),
  bank_code: z.string().trim().max(20).default(''),
  branch_name: z.string().trim().max(120).default(''),
  branch_code: z.string().trim().max(20).default(''),
  bank_account_name: z.string().trim().max(160).default(''),
  bank_account_number: z.string().trim().max(40).optional(),
  bank_account_number_masked: z.string().trim().max(60).optional(),
});

export const vendorProfileSchema = z
  .object({
    business: vendorBusinessProfileSchema,
    contact: vendorContactProfileSchema,
    payment_terms: vendorPaymentTermsProfileSchema,
    delivery_terms: vendorDeliveryTermsProfileSchema,
    return_terms: vendorReturnTermsProfileSchema,
    invoice_terms: vendorInvoiceTermsProfileSchema,
    bank_details: z.array(vendorBankDetailsSchema).max(10).default([]),
  })
  .superRefine((value, context) => {
    const issue = (path: string, message: string) =>
      context.addIssue({ code: z.ZodIssueCode.custom, path: path.split('.'), message });
    const payment = value.payment_terms;
    if (payment.payment_term === 'MONTHLY' && payment.monthly_payment_days === null)
      issue('payment_terms.monthly_payment_days', '月結付款天數為必填。');
    if (payment.payment_term !== 'MONTHLY' && payment.monthly_payment_days !== null)
      issue('payment_terms.monthly_payment_days', '非月結付款條件不得填寫月結天數。');
    if (payment.payment_term === 'CASH_ON_DELIVERY' && payment.payment_method !== 'CASH')
      issue('payment_terms.payment_method', '下貨收現的付款方式固定為現金。');
    if (payment.payment_method === 'OTHER' && !payment.payment_method_other.trim())
      issue('payment_terms.payment_method_other', '其他付款方式說明為必填。');
    if (
      ['CHECK', 'BANK_TRANSFER'].includes(payment.payment_method) &&
      !payment.payment_rule_acknowledged
    )
      issue('payment_terms.payment_rule_acknowledged', '請確認已閱讀付款規則。');

    const delivery = value.delivery_terms;
    if (delivery.delivery_cycle === 'FIXED_WEEKDAYS' && delivery.delivery_weekdays.length === 0)
      issue('delivery_terms.delivery_weekdays', '固定星期至少選擇一項。');
    if (delivery.delivery_cycle === 'OTHER' && !delivery.delivery_cycle_other.trim())
      issue('delivery_terms.delivery_cycle_other', '其他配送週期說明為必填。');
    if (delivery.minimum_order_amount === null)
      issue('delivery_terms.minimum_order_amount', '最低訂購金額為必填；無限制請填 0。');
    if (delivery.minimum_order_quantity === null)
      issue('delivery_terms.minimum_order_quantity', '最低訂購數量為必填；無限制請填 0。');
    if (delivery.freight_rule === 'THRESHOLD' && delivery.freight_threshold_amount === null)
      issue('delivery_terms.freight_threshold_amount', '免運門檻為必填。');
    if (delivery.freight_rule === 'FIXED' && delivery.freight_fixed_amount === null)
      issue('delivery_terms.freight_fixed_amount', '固定運費金額為必填。');
    if (delivery.freight_rule === 'OTHER' && !delivery.freight_other_note.trim())
      issue('delivery_terms.freight_other_note', '其他運費規則說明為必填。');

    const returns = value.return_terms;
    const conditionalFields: Array<[string, string, string]> = [
      ['NEAR_EXPIRY', 'return_near_expiry_condition', '即期退換貨條件為必填。'],
      ['DEFECT', 'return_defect_condition', '瑕疵退換貨條件為必填。'],
      ['INTOLERANCE', 'return_intolerance_condition', '吃不合退換貨條件為必填。'],
      ['OTHER', 'return_other_condition', '其他退換貨條件為必填。'],
    ];
    conditionalFields.forEach(([condition, field, message]) => {
      if (
        returns.return_condition_types.includes(
          condition as (typeof VENDOR_RETURN_CONDITION_TYPES)[number],
        ) &&
        !value.return_terms[field as keyof typeof returns]
      )
        issue(`return_terms.${field}`, message);
    });
    if (
      returns.return_freight_responsibility === 'BY_REASON' &&
      !returns.return_freight_rule_note.trim()
    )
      issue(
        'return_terms.return_freight_rule_note',
        '依退換貨原因判定時，運費負擔規則說明為必填。',
      );

    const invoice = value.invoice_terms;
    if (invoice.invoice_format === 'OTHER' && !invoice.invoice_format_other.trim())
      issue('invoice_terms.invoice_format_other', '其他發票格式說明為必填。');
    if (invoice.invoice_delivery_method === 'OTHER' && !invoice.invoice_delivery_other.trim())
      issue('invoice_terms.invoice_delivery_other', '其他發票寄送方式及原因為必填。');
    if (
      !invoice.invoice_same_as_primary &&
      (!invoice.invoice_contact_name.trim() ||
        !invoice.invoice_contact_phone.trim() ||
        !invoice.invoice_email.trim())
    )
      issue('invoice_terms.invoice_contact_name', '發票聯絡人資料為必填。');
    if (
      !invoice.reconciliation_same_as_primary &&
      (!invoice.reconciliation_contact_name.trim() ||
        !invoice.reconciliation_contact_phone.trim() ||
        !invoice.reconciliation_email.trim())
    )
      issue('invoice_terms.reconciliation_contact_name', '對帳聯絡人資料為必填。');
    if (!invoice.reconciliation_terms_acknowledged)
      issue('invoice_terms.reconciliation_terms_acknowledged', '請確認已閱讀並了解對帳條件。');
    if (payment.payment_method === 'BANK_TRANSFER' && value.bank_details.length === 0)
      issue('bank_details', '付款方式為匯款時，至少需要一筆銀行資料。');
  });

export type VendorProfileInput = z.infer<typeof vendorProfileSchema>;

const vendorContactSchema = z.object({
  name: z.string().trim().max(80).default(''),
  phone: z.string().trim().max(40).default(''),
  email: z.string().trim().email().max(160).optional().or(z.literal('')).default(''),
  title: z.string().trim().max(80).default(''),
});

const vendorFinancialTermsSchema = z.object({
  settlement_method_id: z.string().trim().max(80).default(''),
  settlement_method_other_note: z.string().trim().max(500).default(''),
  payment_terms_id: z.string().trim().max(80).default(''),
  payment_terms_note: z.string().trim().max(1000).default(''),
  payment_method: z.string().trim().max(80).default(''),
  payment_method_note: z.string().trim().max(1000).default(''),
});

const vendorDeliverySettingsSchema = z.object({
  warehouse_delivery_enabled: z.boolean().default(false),
  warehouse_delivery_methods: stringArray,
  warehouse_delivery_cycle: z.string().trim().max(160).default(''),
  warehouse_fixed_delivery_days: stringArray,
  warehouse_order_cutoff_time: z.string().trim().max(20).default(''),
  warehouse_average_delivery_days: optionalInteger,
  warehouse_minimum_order_amount: optionalDecimal,
  warehouse_minimum_order_quantity: optionalInteger,
  warehouse_free_shipping_threshold: optionalDecimal,
  warehouse_shipping_fee: optionalDecimal,
  warehouse_delivery_note: z.string().trim().max(1000).default(''),
  warehouse_receiving_note: z.string().trim().max(1000).default(''),
  store_delivery_enabled: z.boolean().default(false),
  store_delivery_methods: stringArray,
  store_minimum_order_amount: optionalDecimal,
  store_minimum_order_quantity: optionalInteger,
  store_free_shipping_threshold: optionalDecimal,
  store_shipping_fee: optionalDecimal,
  store_delivery_regions: z.string().trim().max(500).default(''),
  store_available_stores: stringArray,
  store_unavailable_stores: stringArray,
  store_fixed_delivery_days: stringArray,
  store_order_cutoff_time: z.string().trim().max(20).default(''),
  store_average_delivery_days: optionalInteger,
  store_delivery_note: z.string().trim().max(1000).default(''),
});

const vendorReturnSettingsSchema = z.object({
  warehouse_return_enabled: z.enum(['YES', 'NO', 'BY_PRODUCT']).default('NO'),
  warehouse_return_methods: stringArray,
  warehouse_return_notice_days: optionalInteger,
  warehouse_return_application_method: z.string().trim().max(160).default(''),
  warehouse_return_minimum_amount: optionalDecimal,
  warehouse_return_shipping_bearer: z.string().trim().max(80).default(''),
  warehouse_return_slip_required: z.boolean().default(false),
  warehouse_vendor_approval_required: z.boolean().default(false),
  warehouse_photo_required: z.boolean().default(false),
  warehouse_good_return_allowed: z.boolean().default(false),
  warehouse_defect_return_allowed: z.boolean().default(false),
  warehouse_near_expiry_return_allowed: z.boolean().default(false),
  warehouse_near_expiry_return_months: optionalInteger,
  warehouse_return_note: z.string().trim().max(1000).default(''),
  store_return_to_vendor_enabled: z.enum(['YES', 'NO', 'BY_PRODUCT']).default('NO'),
  store_return_methods: stringArray,
  store_procurement_approval_required: z.boolean().default(false),
  store_warehouse_approval_required: z.boolean().default(false),
  store_vendor_approval_required: z.boolean().default(false),
  store_photo_required: z.boolean().default(false),
  store_reason_required: z.boolean().default(false),
  store_good_return_allowed: z.boolean().default(false),
  store_defect_return_allowed: z.boolean().default(false),
  store_near_expiry_return_allowed: z.boolean().default(false),
  store_return_deadline_days: optionalInteger,
  store_shipping_bearer: z.string().trim().max(80).default(''),
  store_return_note: z.string().trim().max(1000).default(''),
});

const vendorInvoiceSettingsSchema = z.object({
  invoice_format: z.string().trim().max(80).default(''),
  invoice_format_note: z.string().trim().max(500).default(''),
  invoice_issue_method: z.string().trim().max(80).default(''),
  invoice_delivery_methods: stringArray,
  invoice_email: z.string().trim().email().max(160).optional().or(z.literal('')).default(''),
  invoice_mailing_address: z.string().trim().max(240).default(''),
  invoice_title: z.string().trim().max(160).default(''),
  invoice_tax_id: z.string().trim().max(20).default(''),
  invoice_note: z.string().trim().max(1000).default(''),
});

const vendorBankAccountSchema = z.object({
  id: z.string().trim().max(100).optional(),
  bank_name: z.string().trim().max(120).default(''),
  branch_name: z.string().trim().max(120).default(''),
  bank_code: z.string().trim().max(20).default(''),
  branch_code: z.string().trim().max(20).default(''),
  account_name: z.string().trim().max(160).default(''),
  account_number: z.string().trim().max(40).default(''),
  is_primary: z.boolean().default(false),
  active: z.boolean().default(true),
});

export const loginSchema = z.object({
  username: z.string().trim().min(1).max(80),
  password: z.string().min(1).max(200),
  tax_id: z.string().trim().max(20).optional(),
});

const vendorStrongPasswordSchema = z
  .string()
  .min(8, '密碼至少需要 8 個字元。')
  .max(200, '密碼不可超過 200 個字元。')
  .regex(/[A-Za-z]/, '密碼需包含英文字母。')
  .regex(/[0-9]/, '密碼需包含數字。');

export const vendorAccountLoginSchema = z.object({
  tax_id: z.string().trim().min(1).max(20),
  password: z.string().min(1).max(200),
});

export const vendorPasswordSetupSchema = z
  .object({
    setup_token: z.string().trim().min(20).max(200),
    password: vendorStrongPasswordSchema,
    confirm_password: z.string().max(200),
    turnstile_token: z.string().trim().min(1).max(4000),
  })
  .refine((value) => value.password === value.confirm_password, {
    path: ['confirm_password'],
    message: '兩次輸入的密碼不一致。',
  });

export const vendorAccountActionSchema = z.object({
  action: z.enum(['RESET_PASSWORD', 'UNLOCK', 'DISABLE', 'ENABLE']),
});

export const vendorEntryResolveSchema = z.object({
  tax_id: z.string().trim().min(1).max(20),
});

export const vendorSchema = z.object({
  // 新版主檔使用 profile；以下舊欄位暫保留，供既有草稿及舊資料相容。
  profile: vendorProfileSchema.optional(),
  code: z.string().trim().max(40).optional().default(''),
  name: z.string().trim().max(120).default(''),
  short_name: z.string().trim().max(80).default(''),
  vendor_type: z
    .enum(['MANUFACTURER', 'DISTRIBUTOR', 'AGENT', 'IMPORTER', 'WHOLESALER', 'OTHER'])
    .default('OTHER'),
  tax_id: z.string().trim().max(20).optional().default(''),
  contact_name: z.string().trim().max(80).optional().default(''),
  contact_email: z.string().trim().email().max(160).optional().or(z.literal('')).default(''),
  contact_phone: z.string().trim().max(40).optional().default(''),
  address: z.string().trim().max(240).optional().default(''),
  website: z.string().trim().max(240).default(''),
  fax: z.string().trim().max(40).default(''),
  second_contact_name: z.string().trim().max(80).default(''),
  second_contact_phone: z.string().trim().max(40).default(''),
  notes: z.string().trim().max(2000).default(''),
  internal_notes: z.string().trim().max(2000).default(''),
  responsible_buyer_id: z.string().trim().max(80).nullable().optional().default(null),
  active_date: z.string().trim().max(10).nullable().optional().default(null),
  credit_trade_notes: z.string().trim().max(2000).default(''),
  risk_flag: z.string().trim().max(40).default(''),
  last_reviewed_by: z.string().trim().max(80).nullable().optional().default(null),
  last_reviewed_at: z.string().trim().max(40).nullable().optional().default(null),
  vendor_status: z.enum(['DRAFT', 'ACTIVE', 'SUSPENDED', 'INACTIVE']).optional(),
  contacts: z
    .object({
      primary: vendorContactSchema.default({}),
      secondary: vendorContactSchema.default({}),
    })
    .default({}),
  financial_terms: vendorFinancialTermsSchema.default({}),
  delivery_settings: vendorDeliverySettingsSchema.default({}),
  return_settings: vendorReturnSettingsSchema.default({}),
  invoice_settings: vendorInvoiceSettingsSchema.default({}),
  bank_accounts: z.array(vendorBankAccountSchema).max(10).default([]),
});

export const vendorOptionSchema = z.object({
  option_type: z.enum(['SETTLEMENT_METHOD', 'PAYMENT_TERM']),
  code: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(120),
  sort_order: z.number().int().min(0).max(9999).default(0),
  active: z.boolean().default(true),
});

export type VendorInput = z.infer<typeof vendorSchema>;
export type VendorOptionInput = z.infer<typeof vendorOptionSchema>;

export const vendorStatusSchema = z.object({
  vendor_status: z.enum(['DRAFT', 'ACTIVE', 'SUSPENDED', 'INACTIVE']),
  reason: z.string().trim().min(1, '請填寫狀態變更原因。').max(500),
});

export const vendorMigrationConfirmSchema = z.object({
  reason: z.string().trim().min(2, '請填寫人工確認原因。').max(1000),
});

export const vendorReviewSchema = z
  .object({
    action: z.enum(['APPROVE', 'REQUEST_DOCUMENTS', 'RETURN_FOR_EDIT', 'REJECT']),
    reason: z.string().trim().max(2000).default(''),
    internal_reason: z.string().trim().max(2000).default(''),
    vendor_visible_reason: z.string().trim().max(2000).default(''),
    requested_fields: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    requested_attachment_types: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
    review_due_at: z.string().trim().max(40).default(''),
  })
  .superRefine((value, context) => {
    const vendorReason = value.vendor_visible_reason || value.reason;
    const internalReason = value.internal_reason || value.reason;
    if (['REQUEST_DOCUMENTS', 'RETURN_FOR_EDIT', 'REJECT'].includes(value.action)) {
      if (!vendorReason)
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vendor_visible_reason'],
          message: '請填寫廠商可見的處理說明。',
        });
    }
    if (value.action === 'REJECT' && !internalReason)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['internal_reason'],
        message: '審核不通過時必須填寫原因。',
      });
    if (
      value.action === 'REQUEST_DOCUMENTS' &&
      !value.requested_fields.length &&
      !value.requested_attachment_types.length
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['requested_attachment_types'],
        message: '要求補件時，請至少選擇一個欄位或附件。',
      });
  });

export const vendorApplicationReviewSchema = z.object({
  reason: z.string().trim().max(2000).default(''),
});

export const vendorApplicationDecisionSchema = z
  .object({
    action: z.enum(['APPROVE', 'REQUEST_DOCUMENTS', 'RETURN_FOR_EDIT', 'SUSPEND', 'REJECT']),
    reason: z.string().trim().max(2000).default(''),
    internal_reason: z.string().trim().max(2000).default(''),
    vendor_visible_reason: z.string().trim().max(2000).default(''),
    requested_fields: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    requested_attachment_types: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
    review_due_at: z.string().trim().max(40).default(''),
  })
  .superRefine((value, context) => {
    const internalReason = value.internal_reason || value.reason;
    const vendorReason = value.vendor_visible_reason || value.reason;
    if (['REQUEST_DOCUMENTS', 'RETURN_FOR_EDIT', 'SUSPEND', 'REJECT'].includes(value.action)) {
      if (!vendorReason)
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vendor_visible_reason'],
          message: '請填寫廠商可見的處理說明。',
        });
    }
    if (value.action === 'REJECT' && !internalReason)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['internal_reason'],
        message: '退件時必須填寫內部退件原因。',
      });
    if (
      value.action === 'REQUEST_DOCUMENTS' &&
      !value.requested_fields.length &&
      !value.requested_attachment_types.length
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['requested_fields'],
        message: '要求補件時，請至少選擇一個欄位或附件。',
      });
  });

export const quotationRoundSchema = z.object({
  name: z.string().trim().min(1).max(160),
  product_category_code: z.enum(VENDOR_PRODUCT_CATEGORY_CODES),
  description: z.string().trim().max(2000).default(''),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  launch_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  vendor_ids: z.array(z.string().min(1)).max(100).default([]),
  required_attachment_types: z.array(z.string().min(1)).max(20).default([]),
});

export const quotationRoundArchiveSchema = z.object({
  reason: z.string().max(1000).default(''),
});

export const inviteSchema = z.object({
  quotation_round_id: z.string().min(1),
  vendor_id: z.string().min(1),
  expires_at: z.string().datetime().nullable().optional(),
});

export const productTradeTermsSchema = z.object({
  special_trade_terms: z.preprocess(
    (value) => {
      const normalized = typeof value === 'string' ? value.trim().toLowerCase() : value;
      if (normalized === true || normalized === 1 || normalized === '1' || normalized === 'true')
        return true;
      if (normalized === false || normalized === 0 || normalized === '0' || normalized === 'false')
        return false;
      return value;
    },
    z.boolean({ invalid_type_error: '請選擇本商品是否有特殊交易條件。' }).default(false),
  ),
  special_payment_term: z.string().trim().max(120).default(''),
  special_monthly_payment_days: optionalNumber,
  special_payment_method: z.string().trim().max(120).default(''),
  special_return_scope: z.string().trim().max(40).default(''),
  special_return_handling_method: z.string().trim().max(80).default(''),
  special_return_conditions: z.array(z.string().trim().max(80)).max(10).default([]),
  special_return_deadline: z.string().trim().max(120).default(''),
  special_return_description: z.string().max(2000).default(''),
  special_return_freight_responsibility: z.string().trim().max(80).default(''),
  special_reason: z.string().max(2000).default(''),
});

export const productSchema = z.object({
  vendor_product_code: z.string().trim().max(80).default(''),
  name_zh: z.string().trim().max(160).default(''),
  name_en: z.string().trim().max(160).default(''),
  brand: z.string().trim().max(120).default(''),
  manufacturer: z.string().trim().max(160).default(''),
  origin: z.string().trim().max(80).default(''),
  product_category_code: z.string().trim().max(10).default(''),
  product_category_name: z.string().trim().max(120).default(''),
  product_subcategory_code: z.string().trim().max(20).default(''),
  product_subcategory_name: z.string().trim().max(160).default(''),
  category: z.string().trim().max(80).default(''),
  specification: z.string().trim().max(240).default(''),
  unit: z.string().trim().max(40).default(''),
  case_pack: optionalNumber,
  product_features: z.string().max(3000).default(''),
  ingredients: z.string().max(3000).default(''),
  usage: z.string().max(3000).default(''),
  storage: z.string().max(1000).default(''),
  shelf_life: z.string().max(200).default(''),
  expiry_requirement: z.string().max(500).default(''),
  precautions: z.string().max(2000).default(''),
  target_customers: z.string().max(500).default(''),
  display_location: z.string().max(500).default(''),
  planned_launch_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  storage_method: z.string().max(1000).default(''),
  product_lifecycle: z.string().max(80).default(''),
  product_lifecycle_other_note: z.string().max(500).default(''),
  expected_launch_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  launch_date_is_tentative: z.boolean().default(false),
  no_international_barcode: z.boolean().default(false),
  no_international_barcode_reason: z.string().max(160).default(''),
  no_international_barcode_other_reason: z.string().max(500).default(''),
  barcodes: z.array(z.string().trim().max(32)).max(6).default([]),
  outer_barcode: z.string().trim().max(32).default(''),
  inner_barcode: z.string().trim().max(32).default(''),
  ex_tax_price: optionalNumber,
  tax_included_price: optionalNumber,
  tax_rate: z.number().finite().min(0).max(100).default(5),
  suggested_retail_price: optionalNumber,
  promotional_price: optionalNumber,
  minimum_order_quantity: optionalNumber,
  minimum_order_amount: optionalNumber,
  minimum_delivery_unit: z.string().trim().max(60).default(''),
  delivery_days: optionalNumber,
  payment_method: z.string().trim().max(120).default(''),
  payment_terms: z.string().trim().max(120).default(''),
  return_terms: z.string().max(1000).default(''),
  near_expiry_handling: z.string().max(1000).default(''),
  first_order_offer: z.string().max(1000).default(''),
  gifts: z.string().max(1000).default(''),
  initial_order_offer: z.string().max(40).default(''),
  initial_order_offer_enabled: z.boolean().optional(),
  initial_order_offer_content: z.string().max(1000).default(''),
  gift_bundle_support: z.string().max(40).default(''),
  gift_bundle_support_enabled: z.boolean().optional(),
  gift_bundle_support_content: z.string().max(1000).default(''),
  promotion_support_enabled: z.boolean().optional(),
  promotion_support: z.string().trim().max(40).optional().default(''),
  promotion_content: z.string().max(1000).default(''),
  promotion_start_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  promotion_end_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  trial_support_enabled: z.boolean().optional(),
  sample_support: z.string().trim().max(40).optional().default(''),
  trial_support_content: z.string().max(1000).default(''),
  trade_terms: z.preprocess(
    (value) => (value === null ? undefined : value),
    productTradeTermsSchema.optional(),
  ),
});

export const reviewSchema = z.object({
  required_fields_checked: z.boolean(),
  attachments_checked: z.boolean(),
  internal_comment: z.string().max(3000).default(''),
  risk_level: z.enum(['LOW', 'MEDIUM', 'HIGH', '']).default(''),
  product_category_code: z.string().trim().max(10).default(''),
  product_subcategory_code: z.string().trim().max(20).default(''),
  product_subcategory_change_reason: z.string().max(2000).default(''),
});

export const submissionReturnSchema = z.object({
  reason: z.string().trim().min(1).max(3000),
  item_codes: z
    .array(
      z.enum([
        'PRODUCT_BASIC',
        'BARCODES',
        'PRICES',
        'TRADE_TERMS',
        'INITIAL_OFFER',
        'GIFTS',
        'PROMOTION',
        'SALES_PLAN',
        'ATTACHMENTS',
        'OTHER',
      ]),
    )
    .max(20)
    .default([]),
  revision_due_at: z.string().datetime().nullable().optional(),
});

export const productTradeTermsReviewSchema = z.object({
  decision: z.enum(['ACCEPT', 'REJECT', 'REQUEST_REVISION']),
  reason: z.string().trim().max(2000).default(''),
});

export const batchSubcategorySchema = z.object({
  submission_ids: z.array(z.string().min(1)).min(1).max(100),
  product_subcategory_code: z.string().trim().min(1).max(20),
  reason: z.string().max(2000).default(''),
});

export const storeReviewSchema = z.object({
  attractiveness: z.number().int().min(1).max(5),
  price_acceptance: z.number().int().min(1).max(5),
  customer_fit: z.number().int().min(1).max(5),
  display_feasibility: z.number().int().min(1).max(5),
  sales_potential: z.number().int().min(1).max(5),
  comment: z.string().max(3000).default(''),
});

export const statusSchema = z.object({
  status: z.enum([
    'WAITING_VENDOR',
    'VENDOR_FILLING',
    'BUYER_REVIEW',
    'REVISION_REQUIRED',
    'MEETING_REVIEW',
    'APPROVED',
    'PAUSED',
    'NOT_ADOPTED',
    'MASTER_CREATED',
  ]),
  reason: z.string().max(2000).default(''),
  revision_due_at: z.string().datetime().nullable().optional(),
});

export const commentSchema = z.object({
  content: z.string().trim().min(1).max(3000),
});

export const meetingDecisionSchema = z
  .object({
    submission_id: z.string().min(1),
    decision: z.enum(['APPROVED', 'PAUSED', 'NOT_ADOPTED', 'MASTER_CREATED']),
    notes: z.string().max(3000).default(''),
    product_code: z.string().trim().max(80).default(''),
  })
  .superRefine((value, context) => {
    if (value.decision === 'MASTER_CREATED' && !value.product_code)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['product_code'],
        message: '建立商品主檔時必須填寫正式商品代號。',
      });
  });

const launchNullableDecimal = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  z.number().finite().nonnegative().nullable(),
);
const launchNullablePositiveInteger = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  z.number().int().positive().nullable(),
);
const launchStoreAllocationSchema = z.object({
  store_id: z.string().trim().min(1).max(120),
  allocation_quantity: z.number().int().positive(),
});
const launchAnnouncementItemSchema = z.object({
  submission_id: z.string().trim().min(1).max(160),
  vip_price: launchNullableDecimal.default(null),
  allocation_mode: z.enum(['UNIFORM', 'BY_STORE']).default('UNIFORM'),
  allocation_quantity: launchNullablePositiveInteger.default(null),
  store_allocations: z.array(launchStoreAllocationSchema).max(500).default([]),
});

export const launchAnnouncementSchema = z
  .object({
    title: z.string().trim().min(1).max(200).default('新品上市配貨公告'),
    launch_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .default(null),
    audience_scope: z.enum(['ALL', 'DESIGNATED']).default('ALL'),
    store_ids: z.array(z.string().trim().min(1).max(120)).max(500).default([]),
    items: z.array(launchAnnouncementItemSchema).min(1).max(200),
  })
  .superRefine((value, context) => {
    if (value.audience_scope === 'DESIGNATED' && value.store_ids.length === 0)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['store_ids'],
        message: '指定門市公告至少要選擇一間門市。',
      });
    const submissionIds = new Set<string>();
    value.items.forEach((item, index) => {
      if (submissionIds.has(item.submission_id))
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index, 'submission_id'],
          message: '同一商品不可重複加入公告。',
        });
      submissionIds.add(item.submission_id);
      const allocationStores = new Set<string>();
      item.store_allocations.forEach((allocation, allocationIndex) => {
        if (allocationStores.has(allocation.store_id))
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['items', index, 'store_allocations', allocationIndex, 'store_id'],
            message: '同一商品不可重複設定同一門市。',
          });
        allocationStores.add(allocation.store_id);
      });
    });
  });

export const launchAnnouncementWithdrawSchema = z.object({
  reason: z.string().trim().min(1).max(2000),
});

export const launchAnnouncementAddItemSchema = z.object({
  product_master_id: z.string().trim().min(1).max(160),
  allocation_mode: z.enum(['UNIFORM', 'BY_STORE']).default('UNIFORM'),
  allocation_quantity: launchNullablePositiveInteger.default(null),
  store_allocations: z.array(launchStoreAllocationSchema).max(500).default([]),
});

export const settingsSchema = z.object({
  currency: z.string().trim().min(1).max(10),
  default_tax_rate: z.number().finite().min(0).max(100),
  margin_calculation: z.enum(['RETAIL_MINUS_COST', 'RETAIL_MINUS_TAXED_COST']),
  max_attachment_size_mb: z.number().int().min(1).max(50),
  max_attachment_count: z.number().int().min(1).max(50),
});

export const turnstileSchema = z.object({
  turnstile_token: z.string().min(1).max(4000),
  idempotency_key: z.string().trim().min(16).max(120).optional(),
});

export const proactiveSubmitSchema = turnstileSchema.extend({
  quantified_content_confirmed: z.boolean().default(false),
});

const optionalBoolean = z.boolean().optional().default(false);

const requiredText = (label: string, max = 500) =>
  z.string().trim().min(1, `請填寫${label}。`).max(max);
const applicationDate = (label: string) =>
  requiredText(label, 10).regex(/^\d{4}-\d{2}-\d{2}$/, `${label}格式不正確。`);

const emailOrEmpty = z
  .string()
  .trim()
  .max(160)
  .email('Email 格式不正確')
  .or(z.literal(''))
  .default('');
const applicationCategoryCodes = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'P',
] as const;
const applicationPaymentTerms = ['PREPAID', 'WEEKLY', 'CASH_ON_DELIVERY', 'MONTHLY'] as const;
const applicationPaymentMethods = ['CASH', 'CHECK', 'BANK_TRANSFER', 'OTHER'] as const;
const applicationDeliveryScopes = ['WAREHOUSE', 'STORE', 'BOTH'] as const;
const applicationDeliveryCycles = [
  'DAILY',
  'WEEKLY',
  'FIXED_WEEKDAYS',
  'BIWEEKLY',
  'MONTHLY',
  'ORDER_NOTICE',
  'OTHER',
] as const;
const applicationWeekdays = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;
const applicationFreightRules = [
  'FREE',
  'THRESHOLD',
  'FIXED',
  'ACTUAL',
  'VENDOR_PAYS',
  'COMPANY_PAYS',
  'OTHER',
] as const;
const applicationReturnHandling = ['RETURN', 'EXCHANGE', 'RETURN_OR_EXCHANGE', 'NONE'] as const;
const applicationReturnConditions = ['NEAR_EXPIRY', 'DEFECT', 'INTOLERANCE', 'OTHER'] as const;
const applicationReturnFreight = ['VENDOR', 'COMPANY', 'BY_REASON', 'AGREEMENT', 'OTHER'] as const;
const applicationInvoiceFormats = [
  'ELECTRONIC',
  'PAPER_2',
  'PAPER_3',
  'E_CALCULATOR',
  'CASH_REGISTER',
  'EXEMPT',
  'OTHER',
] as const;
const applicationInvoiceDelivery = ['WITH_GOODS', 'MAIL', 'OTHER'] as const;

const vendorApplicationFields = {
  company_name: requiredText('公司名稱', 160),
  company_short_name: z.string().trim().max(80).optional().default(''),
  // 舊版欄位保留供歷史草稿與既有 API 使用；新流程使用 company_short_name。
  short_name: z.string().trim().max(80).optional().default(''),
  tax_id: requiredText('統一編號', 20),
  phone: requiredText('公司電話', 40),
  address: requiredText('公司地址', 240),
  website: z.string().trim().max(240).optional().default(''),
  company_fax: z.string().trim().max(40).optional().default(''),
  company_email: z.string().trim().email('公司 Email 格式不正確').max(160),
  company_intro: z.string().max(3000).optional().default(''),
  principal_brands: z.array(z.string().trim().min(1).max(160)).max(40).default([]),
  supplied_product_categories: z.array(z.enum(applicationCategoryCodes)).max(15).default([]),
  vendor_closing_day: z.string().trim().max(20).optional().default(''),
  daily_order_cutoff_time: z.string().trim().max(10).optional().default(''),
  payment_term: z.string().trim().max(40).optional().default(''),
  monthly_payment_days: optionalInteger,
  payment_method_other: z.string().trim().max(300).optional().default(''),
  weekly_payment_note: z.string().trim().max(500).optional().default(''),
  payment_rule_acknowledged: optionalBoolean,
  delivery_scope: z.string().trim().max(20).optional().default(''),
  delivery_cycle: z.string().trim().max(30).optional().default(''),
  delivery_weekdays: z.array(z.enum(applicationWeekdays)).max(7).default([]),
  // 舊版欄位保留供歷史草稿與既有 API 使用；新流程使用 delivery_weekdays。
  delivery_fixed_weekdays: z.array(z.enum(applicationWeekdays)).max(7).default([]),
  delivery_cycle_other: z.string().trim().max(300).optional().default(''),
  delivery_minimum_order_amount: optionalNumber,
  delivery_minimum_order_quantity: optionalInteger,
  freight_rule: z.string().trim().max(30).optional().default(''),
  freight_threshold_amount: optionalNumber,
  freight_fixed_amount: optionalNumber,
  freight_other_note: z.string().trim().max(500).optional().default(''),
  return_scope: z.string().trim().max(20).optional().default(''),
  return_handling_method: z.string().trim().max(30).optional().default(''),
  return_condition_types: z.array(z.enum(applicationReturnConditions)).max(4).default([]),
  // 舊版欄位保留供歷史草稿與既有 API 使用；新流程使用 return_condition_types。
  return_conditions: z.array(z.enum(applicationReturnConditions)).max(4).default([]),
  return_near_expiry_condition: z.string().trim().max(500).optional().default(''),
  return_defect_condition: z.string().trim().max(500).optional().default(''),
  return_intolerance_condition: z.string().trim().max(500).optional().default(''),
  return_other_condition: z.string().trim().max(500).optional().default(''),
  return_condition_description: z.string().trim().max(2000).optional().default(''),
  return_freight_responsibility: z.string().trim().max(30).optional().default(''),
  return_freight_rule_note: z.string().trim().max(1000).optional().default(''),
  invoice_same_as_primary: z.boolean().optional().default(true),
  invoice_delivery_other: z.string().trim().max(500).optional().default(''),
  reconciliation_same_as_primary: z.boolean().optional().default(true),
  reconciliation_terms_acknowledged: optionalBoolean,
  invoice_format: z.string().trim().max(40).optional().default(''),
  invoice_format_other: z.string().trim().max(300).optional().default(''),
  invoice_delivery_method: z.string().trim().max(30).optional().default(''),
  invoice_contact_name: z.string().trim().max(80).optional().default(''),
  invoice_contact_phone: z.string().trim().max(40).optional().default(''),
  invoice_email: emailOrEmpty,
  reconciliation_contact_name: z.string().trim().max(80).optional().default(''),
  reconciliation_contact_phone: z.string().trim().max(40).optional().default(''),
  reconciliation_email: emailOrEmpty,
  primary_category_code: z.string().trim().max(10).optional().default(''),
  primary_category_name: z.string().trim().max(120).optional().default(''),
  primary_brand: z.string().trim().max(160).optional().default(''),
  brands: z.string().max(1000).optional().default(''),
  categories: z.string().max(1000).optional().default(''),
  delivery_regions: z.string().max(1000).optional().default(''),
  minimum_order_amount: optionalNumber,
  minimum_order_quantity: optionalNumber,
  settlement_method: z.string().trim().max(120).optional().default(''),
  settlement_method_other: z.string().trim().max(300).optional().default(''),
  payment_terms: z.string().trim().max(120).optional().default(''),
  payment_terms_other: z.string().trim().max(500).optional().default(''),
  payment_method: z.string().trim().max(120).optional().default(''),
  delivery_methods: z.string().max(1000).optional().default(''),
  lead_time: z.string().trim().max(120).optional().default(''),
  return_terms: z.string().max(1000).optional().default(''),
  near_expiry_handling: z.string().max(1000).optional().default(''),
  warehouse_delivery_enabled: z.boolean().optional().default(false),
  warehouse_receiving_method: z.string().trim().max(300).optional().default(''),
  warehouse_delivery_cycle: z.string().trim().max(160).optional().default(''),
  warehouse_order_cutoff_time: z.string().trim().max(40).optional().default(''),
  warehouse_average_delivery_days: optionalInteger,
  warehouse_minimum_order_amount: optionalNumber,
  warehouse_minimum_order_quantity: optionalInteger,
  warehouse_free_shipping_threshold: optionalNumber,
  warehouse_shipping_rule: z.string().trim().max(500).optional().default(''),
  warehouse_return_enabled: z.boolean().optional().default(false),
  warehouse_return_method: z.string().trim().max(300).optional().default(''),
  warehouse_return_notice_days: optionalInteger,
  warehouse_return_shipping_bearer: z.string().trim().max(120).optional().default(''),
  warehouse_good_return_allowed: z.boolean().optional().default(false),
  warehouse_defect_return_allowed: z.boolean().optional().default(false),
  warehouse_near_expiry_return_allowed: z.boolean().optional().default(false),
  warehouse_near_expiry_return_rule: z.string().trim().max(500).optional().default(''),
  store_delivery_enabled: z.boolean().optional().default(false),
  store_receiving_method: z.string().trim().max(300).optional().default(''),
  store_delivery_cycle: z.string().trim().max(160).optional().default(''),
  store_order_cutoff_time: z.string().trim().max(40).optional().default(''),
  store_average_delivery_days: optionalInteger,
  store_minimum_order_amount: optionalNumber,
  store_minimum_order_quantity: optionalInteger,
  store_free_shipping_threshold: optionalNumber,
  store_shipping_rule: z.string().trim().max(500).optional().default(''),
  store_return_to_vendor_enabled: z.boolean().optional().default(false),
  store_return_method: z.string().trim().max(300).optional().default(''),
  store_return_deadline_days: optionalInteger,
  store_return_shipping_bearer: z.string().trim().max(120).optional().default(''),
  store_return_vendor_approval_required: z.boolean().optional().default(false),
  store_return_buyer_approval_required: z.boolean().optional().default(false),
  store_return_route: z.string().trim().max(200).optional().default(''),
  invoice_issue_method: z.string().trim().max(120).optional().default(''),
  reconciliation_cycle: z.string().trim().max(120).optional().default(''),
  bank_name: z.string().trim().max(120).optional().default(''),
  bank_code: z.string().trim().max(20).optional().default(''),
  branch_name: z.string().trim().max(120).optional().default(''),
  branch_code: z.string().trim().max(20).optional().default(''),
  bank_account_name: z.string().trim().max(160).optional().default(''),
  bank_account_number: z.string().trim().max(40).optional().default(''),
  invoice_available: optionalBoolean,
  product_liability_insurance: optionalBoolean,
  notes: z.string().max(2000).optional().default(''),
  contact_name: requiredText('聯絡人姓名', 80),
  contact_job_title: z.string().trim().max(80).optional().default(''),
  // 舊版欄位保留供歷史草稿與既有 API 使用；新流程使用 contact_job_title。
  contact_title: z.string().trim().max(80).optional().default(''),
  contact_phone: requiredText('聯絡電話', 40),
  contact_email: z.string().trim().email('聯絡 Email 格式不正確').max(160),
  submission_mode: z.enum(['DRAFT', 'SUBMIT']).default('SUBMIT'),
};

void applicationDate;

export const vendorApplicationSchema = z
  .object(vendorApplicationFields)
  .superRefine((value, context) => {
    if (value.submission_mode === 'DRAFT') return;
    const deliveryWeekdays = value.delivery_weekdays.length
      ? value.delivery_weekdays
      : value.delivery_fixed_weekdays;
    const returnConditionTypes = value.return_condition_types.length
      ? value.return_condition_types
      : value.return_conditions;
    const addRequired = (key: string, label: string, current: unknown) => {
      if (current === null || current === undefined || String(current).trim() === '')
        context.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: `請填寫${label}。` });
    };
    const addEnum = (key: string, label: string, current: string, allowed: readonly string[]) => {
      if (!allowed.includes(current))
        context.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: `請選擇${label}。` });
    };
    if (value.principal_brands.length === 0)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['principal_brands'],
        message: '至少新增一筆主要代理／品牌。',
      });
    if (value.supplied_product_categories.length === 0)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['supplied_product_categories'],
        message: '請至少選擇一項供應商品類。',
      });
    addRequired('vendor_closing_day', '廠商結帳日', value.vendor_closing_day);
    addRequired('daily_order_cutoff_time', '廠商每日訂單截止時間', value.daily_order_cutoff_time);
    addEnum('payment_term', '付款條件', value.payment_term, applicationPaymentTerms);
    addEnum('payment_method', '付款方式', value.payment_method, applicationPaymentMethods);
    if (value.payment_term === 'MONTHLY' && value.monthly_payment_days === null)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['monthly_payment_days'],
        message: '請填寫月結付款天數。',
      });
    if (value.payment_term === 'CASH_ON_DELIVERY' && value.payment_method !== 'CASH')
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payment_method'],
        message: '下貨收現之付款方式固定為現金。',
      });
    if (value.payment_method === 'OTHER')
      addRequired('payment_method_other', '其他付款方式說明', value.payment_method_other);
    if (
      ['CHECK', 'BANK_TRANSFER'].includes(value.payment_method) &&
      !value.payment_rule_acknowledged
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payment_rule_acknowledged'],
        message: '請勾選已了解付款規則。',
      });
    addEnum('delivery_scope', '配送地點', value.delivery_scope, applicationDeliveryScopes);
    addEnum('delivery_cycle', '廠商配送週期', value.delivery_cycle, applicationDeliveryCycles);
    if (value.delivery_cycle === 'FIXED_WEEKDAYS' && deliveryWeekdays.length === 0)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['delivery_weekdays'],
        message: '請至少選擇一個固定配送星期。',
      });
    if (value.delivery_cycle === 'OTHER')
      addRequired('delivery_cycle_other', '其他配送週期說明', value.delivery_cycle_other);
    if (value.delivery_minimum_order_amount === null)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['delivery_minimum_order_amount'],
        message: '請填寫最低訂購金額；無限制請填 0。',
      });
    if (value.delivery_minimum_order_quantity === null)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['delivery_minimum_order_quantity'],
        message: '請填寫最低訂購數量；無限制請填 0。',
      });
    addEnum('freight_rule', '廠商運費規則', value.freight_rule, applicationFreightRules);
    if (value.freight_rule === 'THRESHOLD' && value.freight_threshold_amount === null)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['freight_threshold_amount'],
        message: '請填寫免運門檻。',
      });
    if (value.freight_rule === 'FIXED' && value.freight_fixed_amount === null)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['freight_fixed_amount'],
        message: '請填寫固定運費金額。',
      });
    if (value.freight_rule === 'OTHER')
      addRequired('freight_other_note', '其他運費規則說明', value.freight_other_note);
    addEnum('return_scope', '退貨地點', value.return_scope, applicationDeliveryScopes);
    addEnum(
      'return_handling_method',
      '退貨處理方式',
      value.return_handling_method,
      applicationReturnHandling,
    );
    if (returnConditionTypes.length === 0)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['return_condition_types'],
        message: '請至少選擇一項退貨／換貨成立條件。',
      });
    if (returnConditionTypes.includes('NEAR_EXPIRY'))
      addRequired(
        'return_near_expiry_condition',
        '即期退換貨條件',
        value.return_near_expiry_condition,
      );
    if (returnConditionTypes.includes('DEFECT'))
      addRequired('return_defect_condition', '瑕疵退換貨條件', value.return_defect_condition);
    if (returnConditionTypes.includes('INTOLERANCE'))
      addRequired(
        'return_intolerance_condition',
        '吃不合退換貨條件',
        value.return_intolerance_condition,
      );
    if (returnConditionTypes.includes('OTHER'))
      addRequired('return_other_condition', '其他退換貨條件', value.return_other_condition);
    addRequired(
      'return_condition_description',
      '退貨／換貨詳細說明',
      value.return_condition_description,
    );
    if (
      /^(依公司規定|請聯絡業務|視情況處理)[。！？!！. ]*$/i.test(
        value.return_condition_description.trim(),
      )
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['return_condition_description'],
        message: '請填寫可供採購作業依據的具體退換貨條件。',
      });
    addEnum(
      'return_freight_responsibility',
      '退貨／換貨運費負擔',
      value.return_freight_responsibility,
      applicationReturnFreight,
    );
    if (value.return_freight_responsibility === 'BY_REASON')
      addRequired('return_freight_rule_note', '運費負擔規則說明', value.return_freight_rule_note);
    addEnum('invoice_format', '發票格式', value.invoice_format, applicationInvoiceFormats);
    if (value.invoice_format === 'OTHER')
      addRequired('invoice_format_other', '其他發票格式說明', value.invoice_format_other);
    addEnum(
      'invoice_delivery_method',
      '發票寄送方式',
      value.invoice_delivery_method,
      applicationInvoiceDelivery,
    );
    if (value.invoice_delivery_method === 'OTHER')
      addRequired('invoice_delivery_other', '其他發票寄送方式及原因', value.invoice_delivery_other);
    if (!value.invoice_same_as_primary) {
      addRequired('invoice_contact_name', '發票聯絡人姓名', value.invoice_contact_name);
      addRequired('invoice_contact_phone', '發票聯絡電話', value.invoice_contact_phone);
      addRequired('invoice_email', '發票聯絡 Email', value.invoice_email);
    }
    if (!value.reconciliation_same_as_primary) {
      addRequired(
        'reconciliation_contact_name',
        '對帳聯絡人姓名',
        value.reconciliation_contact_name,
      );
      addRequired(
        'reconciliation_contact_phone',
        '對帳聯絡電話',
        value.reconciliation_contact_phone,
      );
      addRequired('reconciliation_email', '對帳 Email', value.reconciliation_email);
    }
    if (!value.reconciliation_terms_acknowledged)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reconciliation_terms_acknowledged'],
        message: '請勾選已閱讀並了解對帳條件。',
      });
    const bankRequired = value.payment_method === 'BANK_TRANSFER';
    const bankFields: Array<[keyof typeof value, string]> = [
      ['bank_name', '銀行名稱'],
      ['bank_code', '銀行代碼'],
      ['branch_name', '分行名稱'],
      ['branch_code', '分行代碼'],
      ['bank_account_name', '戶名'],
      ['bank_account_number', '銀行帳號'],
    ];
    if (bankRequired)
      bankFields.forEach(([key, label]) => {
        if (!String(value[key] ?? '').trim())
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: [key],
            message: `請填寫${label}。`,
          });
      });
  });

export const vendorApplicationDraftSchema = z
  .object(vendorApplicationFields)
  .partial()
  .extend({
    company_name: z.string().trim().max(160).default(''),
    company_short_name: z.string().trim().max(80).default(''),
    short_name: z.string().trim().max(80).default(''),
    tax_id: z.string().trim().max(20).default(''),
    phone: z.string().trim().max(40).default(''),
    address: z.string().trim().max(240).default(''),
    company_email: emailOrEmpty,
    primary_category_code: z.string().trim().max(10).default(''),
    primary_brand: z.string().trim().max(160).default(''),
    contact_name: z.string().trim().max(80).default(''),
    contact_job_title: z.string().trim().max(80).default(''),
    contact_title: z.string().trim().max(80).default(''),
    contact_phone: z.string().trim().max(40).default(''),
    contact_email: emailOrEmpty,
    submission_mode: z.literal('DRAFT'),
  });

export type VendorApplicationInput = z.infer<typeof vendorApplicationSchema>;
export type VendorApplicationDraftInput = z.infer<typeof vendorApplicationDraftSchema>;

export const vendorVerificationRequestSchema = z.object({
  mode: z.enum(['EXISTING_VENDOR', 'NEW_VENDOR']),
  tax_id: z.string().trim().min(8).max(20),
  vendor_code: z.string().trim().max(40).optional().default(''),
  email: z.string().trim().email().max(160),
  vendor_application_id: z.string().min(1).optional(),
});

export const vendorVerificationVerifySchema = z
  .object({
    challenge_id: z.string().min(1).max(120),
    code: z
      .string()
      .trim()
      .regex(/^\d{6}$/, '驗證碼必須為 6 位數字')
      .optional(),
    magic_token: z.string().trim().min(20).max(200).optional(),
  })
  .refine((value) => Boolean(value.code || value.magic_token), {
    message: '請輸入驗證碼或驗證連結 Token',
  });

const vendorContactUpdateFields = {
  company_phone: z.string().trim().max(40).optional(),
  company_address: z.string().trim().max(240).optional(),
  company_email: z.string().trim().email().max(160).optional(),
  website: z.string().trim().max(240).optional(),
  company_fax: z.string().trim().max(40).optional(),
  contact_name: z.string().trim().max(80).optional(),
  contact_job_title: z.string().trim().max(80).nullable().optional(),
  contact_phone: z.string().trim().max(40).optional(),
  contact_email: z.string().trim().email().max(160).optional(),
};

export const vendorContactUpdateSchema = z
  .object(vendorContactUpdateFields)
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: '至少需要一項聯絡資料。',
  });

export const vendorChangeRequestSchema = z
  .object({
    change_type: z.enum(['VENDOR_PROFILE', 'PROPOSAL']),
    proposal_id: z.string().trim().max(120).nullable().optional(),
    reason: z.string().trim().min(5).max(2000),
    proposed: z.record(z.string().max(120), z.unknown()).default({}),
  })
  .superRefine((value, context) => {
    if (value.change_type === 'PROPOSAL' && !value.proposal_id)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['proposal_id'],
        message: '新品變更申請缺少 proposal_id。',
      });
  });

export const vendorChangeRequestReviewSchema = z
  .object({
    action: z.enum(['APPROVE', 'REJECT', 'REQUEST_INFO', 'RETURN', 'APPLY']),
    reason: z.string().trim().max(2000).default(''),
  })
  .superRefine((value, context) => {
    if (value.action !== 'APPROVE' && value.action !== 'APPLY' && !value.reason) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reason'],
        message: '此處理動作必須填寫說明。',
      });
    }
  });

export const proactiveProductSchema = z.object({
  vendor_product_code: z.string().trim().max(80).default(''),
  name_zh: z.string().trim().max(160).default(''),
  name_en: z.string().trim().max(160).default(''),
  brand: z.string().trim().max(120).default(''),
  manufacturer: z.string().trim().max(160).default(''),
  origin: z.string().trim().max(80).default(''),
  product_category_code: z.string().trim().max(10).default(''),
  product_category_name: z.string().trim().max(120).default(''),
  product_subcategory_code: z.string().trim().max(20).default(''),
  product_subcategory_name: z.string().trim().max(160).default(''),
  category: z.string().trim().max(80).default(''),
  subcategory: z.string().trim().max(80).default(''),
  specification: z.string().trim().max(240).default(''),
  unit: z.string().trim().max(40).default(''),
  case_pack: optionalNumber,
  product_features: z.string().max(3000).default(''),
  ingredients: z.string().max(3000).default(''),
  usage: z.string().max(3000).default(''),
  storage: z.string().max(1000).default(''),
  storage_method: z.string().max(1000).default(''),
  shelf_life: z.string().max(200).default(''),
  expiry_requirement: z.string().max(500).default(''),
  precautions: z.string().max(2000).default(''),
  target_customers: z.string().max(500).default(''),
  display_location: z.string().max(500).default(''),
  planned_launch_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  expected_launch_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, '預計上市日期格式不正確。')
    .nullable()
    .optional(),
  launch_date_is_tentative: z.boolean().default(false),
  product_lifecycle: z.string().trim().max(40).default('NEW'),
  product_lifecycle_other_note: z.string().max(500).default(''),
  lifecycle_type: z
    .enum([
      'NEW',
      'GROWTH',
      'MATURE',
      'EXISTING',
      'REVISED',
      'NEW_SPEC',
      'NEW_PACKAGE',
      'LIMITED',
      'SEASONAL',
      'DISCONTINUING',
      'ALREADY_LISTED',
      'RESUBMITTED',
      'OTHER',
    ])
    .default('NEW'),
  already_listed_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  listed_duration_months: optionalNumber,
  average_monthly_sales: optionalNumber,
  annual_sales_range: z.string().max(200).default(''),
  main_regions: z.string().max(500).default(''),
  key_features: z.string().max(3000).default(''),
  market_positioning: z.string().max(2000).default(''),
  competitor_difference: z.string().max(2000).default(''),
  suggested_price_rationale: z.string().max(2000).default(''),
  market_sales: z.string().max(2000).default(''),
  estimated_monthly_sales: optionalNumber,
  suggested_store_type: z.string().max(500).default(''),
  display_method: z.string().max(1000).default(''),
  marketing_support: z.string().max(2000).default(''),
  launch_plan: z.string().max(2000).default(''),
  barcodes: z.array(z.string().trim().max(32)).max(6).default([]),
  outer_barcode: z.string().trim().max(32).default(''),
  inner_barcode: z.string().trim().max(32).default(''),
  no_international_barcode: z.boolean().default(false),
  no_international_barcode_reason: z.string().trim().max(160).default(''),
  no_international_barcode_other_reason: z.string().trim().max(500).default(''),
  ex_tax_price: optionalNumber,
  tax_included_price: optionalNumber,
  tax_rate: z.number().finite().min(0).max(100).default(5),
  suggested_retail_price: optionalNumber,
  promotional_price: optionalNumber,
  minimum_order_quantity: optionalNumber,
  minimum_order_amount: optionalNumber,
  minimum_delivery_unit: z.string().trim().max(60).default(''),
  delivery_days: optionalInteger,
  payment_method: z.string().trim().max(120).default(''),
  payment_terms: z.string().trim().max(120).default(''),
  return_terms: z.string().max(1000).default(''),
  near_expiry_handling: z.string().max(1000).default(''),
  first_order_offer: z.string().max(1000).default(''),
  initial_order_offer: z.string().max(1000).default(''),
  initial_order_offer_enabled: z.boolean().optional(),
  initial_order_offer_content: z.string().max(1000).default(''),
  gifts: z.string().max(1000).default(''),
  gift_bundle_support: z.string().max(1000).default(''),
  gift_bundle_support_enabled: z.boolean().optional(),
  gift_bundle_support_content: z.string().max(1000).default(''),
  promotion_support: z.string().max(1000).default(''),
  promotion_support_enabled: z.boolean().optional(),
  promotion_content: z.string().max(1000).default(''),
  promotion_start_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, '促銷開始日期格式不正確。')
    .nullable()
    .optional(),
  promotion_end_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, '促銷結束日期格式不正確。')
    .nullable()
    .optional(),
  display_support: z.string().max(1000).default(''),
  sample_support: z.string().max(1000).default(''),
  trial_support_enabled: z.boolean().optional(),
  trial_support_content: z.string().max(1000).default(''),
  trade_terms: z.preprocess(
    (value) => (value === null ? undefined : value),
    productTradeTermsSchema.optional(),
  ),
  certificate_status: z.enum(['HAS_CERTIFICATE', 'NOT_REQUIRED', '']).optional().default(''),
  certificate_type: z
    .enum([
      'DRUG_LICENSE',
      'TCM_LICENSE',
      'MEDICAL_DEVICE',
      'HEALTH_FOOD',
      'FOOD_REGISTRATION',
      'COSMETIC',
      'ENVIRONMENTAL_AGENT',
      'OTHER',
      '',
    ])
    .optional()
    .default(''),
  certificate_number: z.string().trim().max(160).default(''),
  certificate_other_description: z.string().max(500).default(''),
  certificate_attachment_id: z.string().nullable().optional().default(null),
  certificate_attachment_url: z.string().nullable().optional().default(null),
  certificate_attachment_name: z.string().nullable().optional().default(null),
  certificate_not_required_reason: z.string().max(1000).default(''),
});

/**
 * 檢查是否為無效或測試用的許可證假字號（例如 -, 無, N/A, 待補 等）
 */
export function isDummyCertificateNumber(val?: string | null): boolean {
  if (!val) return true;
  const trimmed = val.trim();
  if (trimmed.length < 2) return true;
  const dummyPatterns = [
    /^[-—_.\s]+$/,
    /^(無|沒有|不知道|待補|未取得|暫無|無字號|申請中|未申請|無認證|無許可證|尚未取得)$/i,
    /^(n\/?a|na|none|null|nil|unknown|tbd|pending|test|demo|no)$/i,
  ];
  return dummyPatterns.some((pattern) => pattern.test(trimmed));
}

export interface ProposalCertificateValidationResult {
  valid: boolean;
  errors: string[];
  blockingItems: string[];
}

/**
 * 驗證新品提案之認證／許可證結構化欄位
 * @param product 提案商品資料
 * @param isFormalSubmit 是否為正式送審（若為草稿則僅驗證已填格式，不強制必填）
 */
export function validateProposalCertificate(
  product?: Record<string, any> | null,
  isFormalSubmit = false,
): ProposalCertificateValidationResult {
  const errors: string[] = [];
  const blockingItems: string[] = [];

  if (!product) {
    if (isFormalSubmit) {
      errors.push('請填寫商品認證／許可證資料。');
      blockingItems.push('認證／許可證資料');
    }
    return { valid: errors.length === 0, errors, blockingItems };
  }

  const status = (product.certificate_status || '').trim();

  if (!status) {
    if (isFormalSubmit) {
      errors.push('請填寫認證／許可證資料。');
      blockingItems.push('認證／許可證資料');
    }
    return { valid: errors.length === 0, errors, blockingItems };
  }

  if (status === 'HAS_CERTIFICATE') {
    const type = (product.certificate_type || '').trim();
    if (!type) {
      if (isFormalSubmit) {
        errors.push('請選擇認證／許可證類型。');
        blockingItems.push('認證／許可證類型');
      }
    } else if (type === 'OTHER' && !product.certificate_other_description?.trim()) {
      if (isFormalSubmit) {
        errors.push('選擇其他認證／許可證時，請填寫其他認證／許可證說明。');
        blockingItems.push('其他認證／許可證說明');
      }
    }

    const number = (product.certificate_number || '').trim();
    if (!number) {
      if (isFormalSubmit) {
        errors.push('請填寫認證／許可證字號。');
        blockingItems.push('認證／許可證字號');
      }
    } else if (isDummyCertificateNumber(number)) {
      errors.push(
        '請填寫真實有效的認證／許可證字號，不得填寫「無」、「N/A」、「待補」等假資料。若尚未取得，請先儲存草稿。',
      );
      blockingItems.push('有效認證／許可證字號');
    }

    if (isFormalSubmit) {
      const hasAttachment = Boolean(
        product.certificate_attachment_id ||
        product.certificate_attachment_url ||
        product.hasCertificateAttachment,
      );
      if (!hasAttachment) {
        errors.push('請上傳認證／許可證附件。');
        blockingItems.push('認證／許可證附件');
      }
    }
  } else if (status === 'NOT_REQUIRED') {
    const reason = (product.certificate_not_required_reason || '').trim();
    if (!reason) {
      if (isFormalSubmit) {
        errors.push('請填寫依法無需認證／許可證的原因。');
        blockingItems.push('無需認證／許可證說明');
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    blockingItems,
  };
}

export const proactiveChannelsSchema = z
  .array(
    z.object({
      channel_code: z.string().trim().min(1).max(40),
      channel_name: z.string().trim().max(160).default(''),
      is_primary: z.boolean().optional().default(false),
    }),
  )
  .max(20)
  .default([]);

export const proactiveProposalSaveSchema = z.object({
  product: z.preprocess((value) => (value === null ? {} : value), proactiveProductSchema),
  channels: proactiveChannelsSchema,
  current_sales_channels: proactiveChannelsSchema.default([]),
  expected_sales_channels: proactiveChannelsSchema.default([]),
});

export const productCategoryUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  sort_order: z.number().int().min(0).max(9999),
  is_active: z.boolean(),
  field_schema: z.record(z.unknown()).optional().default({}),
});

export const attachmentRequirementUpdateSchema = z.object({
  label: z.string().trim().min(1).max(160),
  sort_order: z.number().int().min(0).max(9999),
  required: z.boolean(),
  is_active: z.boolean(),
});

export const productSubcategoryCreateSchema = z.object({
  category_code: z.string().trim().min(1).max(10),
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(160),
  parent_subcategory_code: z.string().trim().max(20).nullable().optional().default(null),
  sort_order: z.number().int().min(0).max(9999),
  is_active: z.boolean().default(true),
});

export const productSubcategoryUpdateSchema = z.object({
  name: z.string().trim().min(1).max(160),
  parent_subcategory_code: z.string().trim().max(20).nullable().optional().default(null),
  sort_order: z.number().int().min(0).max(9999),
  is_active: z.boolean(),
});

export const proactiveReviewSchema = z.object({
  fit_score: z.number().int().min(1).max(5).nullable().optional(),
  duplicate_checked: z.boolean().default(false),
  margin_checked: z.boolean().default(false),
  market_price_checked: z.boolean().default(false),
  moq_checked: z.boolean().default(false),
  delivery_checked: z.boolean().default(false),
  return_checked: z.boolean().default(false),
  regulatory_checked: z.boolean().default(false),
  barcode_data_pending: z.boolean().default(false),
  internal_comment: z.string().max(3000).default(''),
  assigned_buyer_id: z.string().min(1).nullable().optional(),
  product_category_code: z.string().trim().max(10).default(''),
  product_subcategory_code: z.string().trim().max(20).default(''),
  product_subcategory_change_reason: z.string().max(2000).default(''),
});

export const proactiveClassificationSchema = z.object({
  product_category_code: z.string().trim().min(1).max(10),
  product_subcategory_code: z.string().trim().max(20).default(''),
});

export const proactiveRevisionSchema = z.object({
  item_codes: z
    .array(
      z.enum([
        'PRODUCT_BASIC',
        'PRODUCT_CATEGORY',
        'PRODUCT_SUBCATEGORY',
        'BARCODES',
        'PRICES',
        'TRADE_TERMS',
        'INITIAL_OFFER',
        'GIFTS',
        'PROMOTION',
        'TRIAL_SUPPORT',
        'SALES_PLAN',
        'PRODUCT_FRONT_IMAGE',
        'PRODUCT_BACK_IMAGE',
        'BARCODE_IMAGE',
        'QUOTATION',
        'INSPECTION_REPORT',
        'LICENSE',
        'OTHER_ATTACHMENT',
        'OTHER',
      ]),
    )
    .min(1)
    .max(20),
  explanation: z.string().trim().min(1).max(3000),
  due_at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const proactiveStatusSchema = z.object({
  status: z.enum(['WAITING_MEETING', 'APPROVED', 'PAUSED', 'NOT_ADOPTED']),
  reason: z.string().max(3000).default(''),
  reconsideration_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  rejection_reason_code: z
    .enum([
      'DUPLICATE_PRODUCT',
      'MARGIN_TOO_LOW',
      'MARKET_FIT_LOW',
      'DOCUMENT_INCOMPLETE',
      'MOQ_UNSUITABLE',
      'DELIVERY_UNSUITABLE',
      'OTHER',
    ])
    .optional(),
  internal_rejection_reason: z.string().max(2000).default(''),
  vendor_visible_reason: z.string().max(2000).default(''),
  meeting_strategy: z
    .object({
      product_strategy: z.enum(['REPLACE', 'COEXIST', 'TRIAL', 'LIMITED_STORE', 'NEW_CATEGORY']),
      replaced_product_id: z.string().trim().max(100).nullable().optional(),
      replaced_product_handling: z
        .enum(['SELL_OUT', 'RETURN_TO_SUPPLIER', 'STORE_TRANSFER', 'PROMO_CLEARANCE', 'OTHER'])
        .nullable()
        .optional(),
      replaced_product_handling_other: z.string().trim().max(1000).default(''),
      coexistence_reason: z.string().trim().max(2000).default(''),
    })
    .optional(),
});

const productMasterCodePart = (label: string) =>
  z
    .string({ required_error: `請填寫${label}。` })
    .trim()
    .min(1, `請填寫${label}。`)
    .max(20, `${label}不得超過 20 碼。`)
    .regex(/^[A-Za-z0-9]+$/, `${label}只能使用英文字母或數字。`);

export const productMasterCreateSchema = z.object({
  large_code: productMasterCodePart('商品大碼'),
  middle_code: productMasterCodePart('商品中碼'),
  small_code: productMasterCodePart('商品小碼'),
  vip_price: z
    .number({ required_error: '請填寫 VIP價。', invalid_type_error: 'VIP價格式不正確。' })
    .finite()
    .nonnegative('VIP價不得小於 0。'),
  employee_price: z.number().finite().nonnegative('員工價不得小於 0。').nullable().optional(),
  in_store_promotion: z.string().trim().max(2000).default(''),
});

export const proactiveConversionSchema = z.object({
  quotation_round_id: z.string().min(1).optional(),
  reason: z.string().max(2000).default(''),
});

export const proactiveSettingsSchema = settingsSchema.extend({
  proactive_enabled: z.boolean(),
  proactive_daily_max: z.number().int().min(1).max(10000),
  proactive_vendor_daily_max: z.number().int().min(1).max(1000),
  proactive_max_file_size_mb: z.number().int().min(1).max(100),
  proactive_max_file_count: z.number().int().min(1).max(100),
  proactive_allowed_file_types: z.array(z.string().min(1)).min(1).max(30),
  proactive_completeness_threshold: z.number().int().min(0).max(100),
  proactive_numbering_rule: z.string().trim().min(1).max(80),
  proactive_allow_new_vendors: z.boolean(),
  proactive_require_email_verification: z.boolean(),
});
